interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

interface CacheConfig {
  defaultTTL?: number; // Default time to live in milliseconds (5 minutes)
  maxSize?: number; // Maximum number of items in cache
}

class CacheService {
  private cache = new Map<string, CacheItem<any>>();
  private config: Required<CacheConfig>;

  constructor(config: CacheConfig = {}) {
    this.config = {
      defaultTTL: config.defaultTTL || 5 * 60 * 1000, // 5 minutes default
      maxSize: config.maxSize || 100, // 100 items default
    };
  }

  /**
   * שמירת פריט במטמון
   */
  set<T>(key: string, data: T, ttl?: number): void {
    // ניקוי פריטים פגי תוקף
    this.cleanup();

    // בדיקה אם המטמון מלא
    if (this.cache.size >= this.config.maxSize) {
      this.evictOldest();
    }

    // בדיקה אם הפריט כבר קיים (למניעת שמירה כפולה)
    const existingItem = this.cache.get(key);
    if (existingItem) {
      // בדיקה אם הנתונים זהים
      const dataString = JSON.stringify(data);
      const existingString = JSON.stringify(existingItem.data);

      if (dataString === existingString) {
                return;
      }

      // אם הפריט קיים אבל עם תוכן שונה, עדכן אותו
          }

    const item: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.config.defaultTTL,
    };

    this.cache.set(key, item);
  }

  /**
   * שליפת פריט מהמטמון
   */
  get<T>(key: string): T | null {
    const item = this.cache.get(key);

    if (!item) {
      return null;
    }

    // בדיקה אם הפריט פג תוקף
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  /**
   * בדיקה אם פריט קיים במטמון
   */
  has(key: string): boolean {
    const item = this.cache.get(key);
    if (!item) return false;

    // בדיקה אם הפריט פג תוקף
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * מחיקת פריט מהמטמון
   */
  delete(key: string): boolean {
    const deleted = this.cache.delete(key);
    return deleted;
  }

  /**
   * ניקוי כל המטמון
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * קבלת גודל המטמון
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * קבלת כל המפתחות במטמון
   */
  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * קבלת מידע מפורט על פריט במטמון
   */
  getInfo(key: string): {
    exists: boolean;
    timestamp?: number;
    ttl?: number;
    age?: number;
  } | null {
    const item = this.cache.get(key);
    if (!item) {
      return { exists: false };
    }

    const now = Date.now();
    const age = now - item.timestamp;

    return {
      exists: true,
      timestamp: item.timestamp,
      ttl: item.ttl,
      age: age,
    };
  }

  /**
   * ניקוי פריטים פגי תוקף
   */
  private cleanup(): void {
    const now = Date.now();
    this.cache.forEach((item, key) => {
      if (now - item.timestamp > item.ttl) {
        this.cache.delete(key);
      }
    });
  }

  /**
   * מחיקת הפריט הישן ביותר
   */
  private evictOldest(): void {
    let oldestKey = "";
    let oldestTimestamp = Number.POSITIVE_INFINITY;

    this.cache.forEach((item, key) => {
      if (item.timestamp < oldestTimestamp) {
        oldestTimestamp = item.timestamp;
        oldestKey = key;
      }
    });

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  /**
   * שמירת נתונים עם מפתח מורכב
   */
  setWithPrefix<T>(prefix: string, key: string, data: T, ttl?: number): void {
    const fullKey = `${prefix}:${key}`;
    this.set(fullKey, data, ttl);
  }

  /**
   * שליפת נתונים עם מפתח מורכב
   */
  getWithPrefix<T>(prefix: string, key: string): T | null {
    const fullKey = `${prefix}:${key}`;
    return this.get<T>(fullKey);
  }

  /**
   * מחיקת כל הפריטים עם prefix מסוים
   */
  deleteWithPrefix(prefix: string): void {
    const keysToDelete = this.keys().filter((key) =>
      key.startsWith(`${prefix}:`)
    );
    keysToDelete.forEach((key) => this.delete(key));
  }
}

// יצירת מופע גלובלי של שירות המטמון
export const cacheService = new CacheService();

// יצירת מופעים מיוחדים למטרות שונות
export const workshopCache = new CacheService({
  defaultTTL: 10 * 60 * 1000, // 10 minutes for workshops
  maxSize: 50,
});

export const userCache = new CacheService({
  defaultTTL: 30 * 60 * 1000, // 30 minutes for user data
  maxSize: 20,
});

export const imageCache = new CacheService({
  defaultTTL: 60 * 60 * 1000, // 1 hour for images
  maxSize: 100,
});

export default CacheService;
